import React, { useEffect, useState, useCallback } from "react";
import { MessagesSquare, User, Users, Circle, Hand, RefreshCw, Bell } from "lucide-react";
import { toast } from "sonner";
import { staffChatApi, adminChatApi, apiError, isAdmin } from "../../lib/api";
import { ChatThread } from "../../components/ChatThread";
import { subscribeToPush } from "../../lib/push";
import { Switch } from "../../components/ui/switch";

export default function ChatConsole() {
  const admin = isAdmin();
  const [queue, setQueue] = useState([]);
  const [active, setActive] = useState(null);
  const [available, setAvailable] = useState(true);
  const [pushOn, setPushOn] = useState(typeof Notification !== "undefined" && Notification.permission === "granted");

  const loadQueue = useCallback(async () => {
    try { setQueue(await staffChatApi.queue()); } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadQueue();
    subscribeToPush();
    staffChatApi.getAvailability().then((a) => setAvailable(a.my_available)).catch(() => {});
    const iv = setInterval(loadQueue, 3000);
    return () => clearInterval(iv);
  }, [loadQueue]);

  const enablePush = async () => {
    try {
      if (typeof Notification === "undefined") { toast.error("Tu navegador no soporta avisos."); return; }
      const perm = await Notification.requestPermission();
      if (perm === "granted") { await subscribeToPush(); setPushOn(true); toast.success("Avisos activados. Te notificaremos cada chat nuevo."); }
      else toast.error("Activa los permisos de notificaciones en tu navegador.");
    } catch { toast.error("No se pudieron activar los avisos."); }
  };

  const open = async (c) => {
    try { setActive(await staffChatApi.conversation(c.id)); } catch (e) { toast.error(apiError(e)); }
  };

  const claim = async () => {
    try { const c = await staffChatApi.claim(active.id); setActive(c); toast.success("Conversación asignada a ti."); loadQueue(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleAvail = async (v) => {
    setAvailable(v);
    try { await staffChatApi.setAvailability(v); toast.success(v ? "Staff disponible." : "Staff marcado como no disponible."); }
    catch (e) { toast.error(apiError(e)); setAvailable(!v); }
  };

  const needsClaim = active && (active.status === "waiting" || !active.staff);

  return (
    <div data-testid="chat-console">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-extrabold text-[#0A1C30] flex items-center gap-2"><MessagesSquare className="w-6 h-6 text-[#E9A400]" /> Chat Privado Supera — Bandeja</h1>
        <div className="flex items-center gap-3">
          {!pushOn && (
            <button data-testid="enable-push-btn" onClick={enablePush} className="inline-flex items-center gap-1.5 rounded-xl bg-[#0A1C30]/5 hover:bg-[#0A1C30]/10 text-[#0A1C30] font-bold px-3 py-2 text-sm transition-colors">
              <Bell className="w-4 h-4 text-[#E9A400]" /> Activar avisos
            </button>
          )}
          <label className="flex items-center gap-2 bg-white rounded-xl ring-1 ring-black/5 px-4 py-2">
            <Circle className={`w-3 h-3 ${available ? "fill-[#22A860] text-[#22A860]" : "fill-[#C0392B] text-[#C0392B]"}`} />
            <span className="text-sm font-bold text-[#0A1C30]">{available ? "Disponible" : "No disponible"}</span>
            <Switch data-testid="availability-switch" checked={available} onCheckedChange={toggleAvail} />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 h-[calc(100vh-11rem)]">
        {/* Queue */}
        <div className="bg-white rounded-2xl ring-1 ring-black/5 overflow-y-auto">
          <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between sticky top-0 bg-white z-10">
            <span className="font-extrabold text-[#0A1C30] text-sm">Conversaciones ({queue.length})</span>
            <button onClick={loadQueue} className="text-[#6B7688] hover:text-[#0A1C30]"><RefreshCw className="w-4 h-4" /></button>
          </div>
          <div data-testid="queue-list">
            {queue.length === 0 && <p className="text-sm text-[#6B7688] p-4">No hay conversaciones en la bandeja.</p>}
            {queue.map((c) => (
              <button key={c.id} data-testid={`queue-item-${c.id}`} onClick={() => open(c)} className={`w-full text-left px-4 py-3 border-b border-black/5 flex items-center gap-3 transition-colors ${active?.id === c.id ? "bg-[#FAF8F1]" : "hover:bg-[#F4F6F9]"}`}>
                <span className="w-10 h-10 rounded-full bg-navy flex items-center justify-center flex-shrink-0">{c.kind === "family" ? <Users className="w-5 h-5 text-gold" /> : <User className="w-5 h-5 text-gold" />}</span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2"><span className="font-bold text-[#0A1C30] text-sm truncate">{c.owner_name}</span>
                    {c.status === "waiting" && <span className="text-[9px] font-bold text-[#E9A400] bg-[#FBF0CE] px-1.5 py-0.5 rounded-full">Nuevo</span>}
                  </span>
                  <span className="block text-xs text-[#6B7688] truncate">{c.owner_programa} · {c.last_message_preview || "—"}</span>
                </span>
                {c.unread > 0 && <span className="w-2.5 h-2.5 rounded-full bg-[#E23B3B]" />}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className="bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden">
          {active ? (
            <ChatThread conversation={active} ctx={staffChatApi} allowCopy={admin} showUserInfo
              extraActions={needsClaim ? <button data-testid="claim-btn" onClick={claim} className="inline-flex items-center gap-1 rounded-full bg-[var(--gold)] text-[#0A1C30] text-xs font-bold px-3 py-1.5"><Hand className="w-3.5 h-3.5" /> Atender</button> : null}
              onUpdated={(c) => { setActive(c); loadQueue(); }} />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-[#6B7688]">
              <MessagesSquare className="w-12 h-12 text-[#C7CDD6]" />
              <p className="mt-3 font-semibold">Selecciona una conversación para atender.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
